/**
 * Task #3822 — Progetti del PA (spazio di lavoro condiviso).
 *
 * DTO condivisi (Zod + z.infer) fra backend e frontend per i Progetti del
 * Personal Assistant: contenitori tenant-scoped con istruzioni proprie,
 * knowledge propria, skill proprie, conversazioni, artefatti e membri.
 *
 * Guardrail: il progetto condivide CONTESTO, mai poteri — nessun DTO qui
 * trasporta permessi/tool; il perimetro effettivo del PA di ciascun membro
 * resta l'unica autorità sulle azioni.
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// Base
// ---------------------------------------------------------------------------

export const PaProjectRoleSchema = z.enum(['owner', 'member']);
export type PaProjectRole = z.infer<typeof PaProjectRoleSchema>;

export const PaProjectStatusSchema = z.enum(['active', 'archived']);
export type PaProjectStatus = z.infer<typeof PaProjectStatusSchema>;

/** Riga progetto come vista dall'utente corrente (myRole = ruolo di chi chiama). */
export const PaProjectSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  instructions: z.string().nullable(),
  status: PaProjectStatusSchema,
  ownerUserId: z.string().uuid(),
  ownerName: z.string().nullable(),
  /** ai_agents nascosto che contiene la knowledge del progetto (NULL finché nessun documento). */
  knowledgeAgentId: z.string().uuid().nullable(),
  myRole: PaProjectRoleSchema,
  memberCount: z.number().int().nonnegative(),
  conversationCount: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PaProject = z.infer<typeof PaProjectSchema>;

export const PaProjectMemberSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  role: PaProjectRoleSchema,
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  invitedBy: z.string().uuid().nullable(),
  createdAt: z.string(),
});
export type PaProjectMember = z.infer<typeof PaProjectMemberSchema>;

/** Skill Claude agganciata al progetto (contratto #3821: junction verso claude_skills). */
export const PaProjectSkillSchema = z.object({
  skillId: z.string().uuid(),
  displayTitle: z.string(),
  description: z.string().nullable(),
  source: z.string().nullable(),
  latestVersion: z.string().nullable(),
});
export type PaProjectSkill = z.infer<typeof PaProjectSkillSchema>;

/** Documento knowledge del progetto (riga ai_agent_documents del knowledge agent). */
export const PaProjectKnowledgeDocSchema = z.object({
  id: z.string().uuid(),
  filename: z.string(),
  fileType: z.string().nullable(),
  fileSize: z.number().int().nullable(),
  status: z.string(),
  chunkCount: z.number().int().nonnegative(),
  createdAt: z.string(),
});
export type PaProjectKnowledgeDoc = z.infer<typeof PaProjectKnowledgeDocSchema>;

/** Conversazione dentro un progetto (di qualunque membro: userId identifica l'autore). */
export const PaProjectConversationSchema = z.object({
  id: z.string().uuid(),
  title: z.string().nullable(),
  userId: z.string().uuid(),
  userName: z.string().nullable(),
  status: z.string(),
  messageCount: z.number().int().nonnegative(),
  lastMessageAt: z.string().nullable(),
  createdAt: z.string(),
});
export type PaProjectConversation = z.infer<typeof PaProjectConversationSchema>;

/** Messaggio in vista read-only (conversazioni di altri membri del progetto). */
export const PaProjectConversationMessageSchema = z.object({
  id: z.string().uuid(),
  role: z.string(),
  content: z.string().nullable(),
  createdAt: z.string(),
});
export type PaProjectConversationMessage = z.infer<typeof PaProjectConversationMessageSchema>;

export const PaProjectDetailSchema = PaProjectSchema.extend({
  members: z.array(PaProjectMemberSchema),
  skills: z.array(PaProjectSkillSchema),
  knowledgeDocs: z.array(PaProjectKnowledgeDocSchema),
});
export type PaProjectDetail = z.infer<typeof PaProjectDetailSchema>;

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

export const CreatePaProjectRequestSchema = z.object({
  name: z.string().min(1).max(120),
  description: z.string().max(2000).optional(),
  instructions: z.string().max(20000).optional(),
});
export type CreatePaProjectRequest = z.infer<typeof CreatePaProjectRequestSchema>;

export const UpdatePaProjectRequestSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  description: z.string().max(2000).nullable().optional(),
  instructions: z.string().max(20000).nullable().optional(),
  status: PaProjectStatusSchema.optional(),
});
export type UpdatePaProjectRequest = z.infer<typeof UpdatePaProjectRequestSchema>;

export const AddPaProjectMemberRequestSchema = z.object({
  userId: z.string().uuid(),
  role: PaProjectRoleSchema.default('member'),
});
export type AddPaProjectMemberRequest = z.infer<typeof AddPaProjectMemberRequestSchema>;

export const SetPaProjectSkillsRequestSchema = z.object({
  skillIds: z.array(z.string().uuid()).max(8),
});
export type SetPaProjectSkillsRequest = z.infer<typeof SetPaProjectSkillsRequestSchema>;

/**
 * Risultato della ricerca utenti del tenant per l'invito a un progetto
 * (route `/users/search`, shape row snake_case del backend).
 */
export const TenantUserSearchResultSchema = z.object({
  id: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  email: z.string().nullable(),
});
export type TenantUserSearchResult = z.infer<typeof TenantUserSearchResultSchema>;

// ---------------------------------------------------------------------------
// Contratto Routine → Progetto (#3038 — solo il contratto, non l'implementazione)
// ---------------------------------------------------------------------------

/**
 * Contratto dati per l'associazione routine→progetto (implementazione in
 * #3038): la futura tabella routine porterà `project_id uuid NULL` con FK
 * `ai_assistant_projects(id) ON DELETE SET NULL`; una routine associata a un
 * progetto NE EREDITA il contesto (istruzioni + knowledge + skill) con le
 * stesse regole delle conversazioni, e le sue esecuzioni girano SEMPRE col
 * perimetro poteri del PA dell'utente proprietario della routine, mai
 * ampliato dal progetto.
 */
export const PaProjectRoutineContractSchema = z.object({
  routineId: z.string().uuid(),
  projectId: z.string().uuid().nullable(),
  /** Il contesto del progetto viene iniettato nelle esecuzioni della routine. */
  inheritsProjectContext: z.literal(true),
});
export type PaProjectRoutineContract = z.infer<typeof PaProjectRoutineContractSchema>;
